import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface Pizza {
  id: string;
  name: string;
  capture_at?: string | null | undefined;
}

type Direction = "next" | "prev";

/** A move to the next/previous pizza of the list, resolved against one page of it. */
interface PizzaMove {
  direction: Direction;
  /** pizza the move starts from */
  fromId: string;
  /** page the move is resolved on */
  page: number;
  /** index on that page used when the start pizza is not on it; null = count its position first */
  fallbackIndex: number | "last" | null;
  /** when the start pizza's position was counted: the page must be loaded after it (ms epoch) */
  since: number;
}

type MoveResult =
  | { type: "open"; pizzaId: string }
  | { type: "page"; move: PizzaMove }
  /** count the start pizza's position; listed = it is on the loaded page */
  | { type: "locate"; listed: boolean }
  | { type: "none" };

/**
 * Where a move ends on the loaded page. The step is taken from the start pizza wherever it
 * is on the page (after a refetch it can sit elsewhere or show up on two pages); a target
 * past the end of the page continues on the neighbouring page, from a counted position.
 */
export function resolvePizzaMove(
  pizzas: { id: string }[],
  totalPages: number | undefined,
  move: PizzaMove,
): MoveResult {
  const forward = move.direction === "next";
  const idx = pizzas.findIndex((p) => p.id === move.fromId);
  let target: number;
  if (idx !== -1) {
    target = idx + (forward ? 1 : -1);
  } else if (move.fallbackIndex === null) {
    return { type: "locate", listed: false };
  } else {
    target =
      move.fallbackIndex === "last" ? pizzas.length - 1 : move.fallbackIndex;
  }

  if (target >= 0 && target < pizzas.length) {
    return { type: "open", pizzaId: pizzas[target].id };
  }
  // Past the edge of the page. The neighbouring page lines up with this one only when both were
  // loaded together, but a page stays on screen for minutes while its pizzas are checked and new
  // captures shift the list meanwhile (2026-11-03: "Next" at the end of a page skipped pages).
  if (move.fallbackIndex === null) {
    return { type: "locate", listed: true };
  }
  if (forward) {
    return totalPages !== undefined && move.page + 1 < totalPages
      ? {
          type: "page",
          move: { ...move, page: move.page + 1, fallbackIndex: 0 },
        }
      : { type: "none" };
  }
  // the page is shorter than the located position expected (the list shrank)
  if (target >= pizzas.length && pizzas.length > 0) {
    return { type: "open", pizzaId: pizzas[pizzas.length - 1].id };
  }
  return move.page > 0
    ? {
        type: "page",
        move: { ...move, page: move.page - 1, fallbackIndex: "last" },
      }
    : { type: "none" };
}

export const isInteractiveTarget = (target: EventTarget | null) =>
  target instanceof Element &&
  !!target.closest(
    'input, textarea, select, [contenteditable="true"], [role="slider"], [role="listbox"], [role="menu"]',
  );

interface UsePizzaNavigationProps {
  pizza?: Pizza | null;
  pizzas?: Pizza[] | null;
  /** the loaded page is being (re)fetched */
  fetching?: boolean;
  /** when the loaded page was fetched (ms epoch) */
  updatedAt?: number;
  /** fetch the loaded page again */
  refetch?: () => void;
  page: number;
  perPage: number;
  totalPages?: number;
  setPage: (page: number) => void;
  /** position of a pizza in the whole list, for a pizza that is not on the loaded page */
  locatePizza: (pizza: Pizza, options?: { fresh?: boolean }) => Promise<number | null>;
  /** false = no keyboard navigation (the detail browses something else, e.g. an order's pizzas) */
  enabled?: boolean;
}

export function usePizzaNavigation({
  pizza,
  pizzas,
  fetching = false,
  updatedAt = 0,
  refetch,
  page,
  perPage,
  totalPages,
  setPage,
  locatePizza,
  enabled = true,
}: UsePizzaNavigationProps) {
  // a move waiting for its page of the list to load
  const [pendingMove, setPendingMove] = useState<PizzaMove | null>(null);
  const locatingRef = useRef(false);
  // the pending move a refetch of an outdated page was asked for (once per move)
  const refetchedForRef = useRef<PizzaMove | null>(null);
  const navigate = useNavigate();
  // The pizza a move starts from is the one in the URL, read when the move starts. The router renders a
  // navigation as a transition and the detail loads after it, so for a moment the rendered pizza is the
  // previous one: a click then stepped from it, opened the pizza already open and was lost.
  const { pizzaId: routePizzaId } = useParams();
  const currentId = () =>
    window.location.pathname.match(/^\/pizzas\/([^/]+)/)?.[1] ?? routePizzaId ?? pizza?.id ?? null;
  // its data for counting its position: the list row, or the detail once it is loaded
  const startPizza = (id: string) =>
    pizzas?.find((p) => p.id === id) ?? (pizza && pizza.id === id ? pizza : null);

  // Generate pizza detail route
  const getLink = (pizzaId: string) => `/pizzas/${pizzaId}`;

  const locate = (move: PizzaMove, current: Pizza, listed: boolean) => {
    if (locatingRef.current) return;
    locatingRef.current = true;
    const since = Date.now();
    locatePizza(current, { fresh: true })
      .then((position) => {
        if (position === null) return;
        // Where to look. A pizza on the list: "next" one place after it, "prev" its own place, so that
        // the step is taken from it on that page (two pizzas of one frame share capture_at and the
        // count puts the second one at the first one's place; a capture after the count moves it on).
        // A pizza that is not in the list (any more) has the next one at its place.
        const target = position + (listed ? 1 : 0) - (move.direction === "prev" ? 1 : 0);
        const targetPage = Math.floor(target / perPage);
        if (target < 0 || (totalPages !== undefined && targetPage >= totalPages)) return;
        const located: PizzaMove = {
          ...move,
          page: targetPage,
          fallbackIndex: target % perPage,
          since,
        };
        setPendingMove(located);
        setPage(located.page);
      })
      .catch(() => undefined)
      .finally(() => {
        locatingRef.current = false;
      });
  };

  const runMove = (move: PizzaMove, loaded: Pizza[]) => {
    const result = resolvePizzaMove(loaded, totalPages, move);
    if (result.type === "page") {
      setPendingMove(result.move);
      setPage(result.move.page);
      return;
    }
    setPendingMove(null);
    if (result.type === "open") {
      navigate(getLink(result.pizzaId));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (result.type === "locate") {
      const start = startPizza(move.fromId);
      if (start) locate(move, start, result.listed);
    }
  };

  const go = (direction: Direction) => {
    const fromId = currentId();
    if (!fromId || !pizzas) return;
    runMove({ direction, fromId, page, fallbackIndex: null, since: 0 }, pizzas);
  };

  // handlers bound to the latest render (the keyboard listener is registered once)
  const goRef = useRef(go);
  useEffect(() => {
    goRef.current = go;
  });

  const goToNextPizza = useCallback(() => goRef.current("next"), []);
  const goToPrevPizza = useCallback(() => goRef.current("prev"), []);

  // Keyboard navigation effect
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.defaultPrevented ||
        e.altKey ||
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey ||
        isInteractiveTarget(e.target)
      ) {
        return;
      }
      if (e.key === "ArrowRight") {
        goToNextPizza();
      } else if (e.key === "ArrowLeft") {
        goToPrevPizza();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, goToNextPizza, goToPrevPizza]);

  // Finish a move once its page is loaded
  useEffect(() => {
    if (!pendingMove) return;
    // another pizza was opened or the list was paged/filtered meanwhile
    if (currentId() !== pendingMove.fromId || page !== pendingMove.page) {
      setPendingMove(null);
      return;
    }
    // The page has to be loaded after the position was counted. A page seen in the last minutes comes
    // from the cache first (refetched in the background) and the page on screen can be minutes old;
    // new captures shift the list meanwhile, and a move resolved on such a copy opened a pizza as many
    // places too far as pizzas were captured since.
    if (!pizzas || fetching) return;
    if (updatedAt < pendingMove.since && refetch && refetchedForRef.current !== pendingMove) {
      refetchedForRef.current = pendingMove;
      refetch();
      return;
    }
    runMove(pendingMove, pizzas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMove, pizzas, fetching, updatedAt, page, routePizzaId, pizza]);

  return {
    goToNextPizza,
    goToPrevPizza,
  };
}
